// ============================================================
// TeamsContext.js
// Surface detection — is the SPA running inside a Microsoft Teams
// host (tab / personal app) or a plain browser?
//
// @microsoft/teams-js is imported DYNAMICALLY (import()) — a static
// import throws when evaluated outside a Teams host, which would
// break every non-Teams page load. The dynamic import + app
// .initialize() resolving is the only reliable "are we in Teams"
// signal; outside Teams it rejects and we fall back to browser mode.
//
// `ready` distinguishes "detection still running" from "detected:
// not in Teams". Consumers that redirect on !isTeams (e.g.
// TeamsEntryPoint) MUST wait for `ready` first, otherwise they
// redirect during the async detection window even when genuinely
// inside Teams.
// ============================================================

import { createContext, useContext, useState, useEffect } from 'react';

const TeamsContext = createContext({ isTeams: false, teamsContext: null, ready: false });

export function TeamsProvider({ children }) {
  const [isTeams, setIsTeams] = useState(false);
  const [teamsContext, setTeamsContext] = useState(null);
  const [ready, setReady] = useState(false);

  useEffect(() => {
    let cancelled = false;
    import('@microsoft/teams-js')
      .then(({ app }) => app.initialize().then(() => app.getContext()))
      .then((ctx) => {
        if (cancelled) return;
        setIsTeams(true);
        setTeamsContext(ctx);
        setReady(true);
      })
      .catch(() => {
        // Not in Teams (or teams-js unavailable) — browser context.
        if (cancelled) return;
        setIsTeams(false);
        setReady(true);
      });
    return () => { cancelled = true; };
  }, []);

  return (
    <TeamsContext.Provider value={{ isTeams, teamsContext, ready }}>
      {children}
    </TeamsContext.Provider>
  );
}

export const useTeams = () => useContext(TeamsContext);
